<template>
    <div class="ssh-page">
        <!-- 登录使用功能的提示 -->
        <div v-if="this.$store.state.user.token == undefined || this.$store.state.user.token == ''"
            class="ssh-notice no-select">
            <el-icon class="ssh-notice__icon">
                <Monitor />
            </el-icon>
            <span>登录后可执行共享的 SSH 命令</span>
        </div>
        <!-- 团队筛选 + 搜索过滤 -->
        <div class="ssh-toolbar no-select">
            <div v-if="isLogin" class="ssh-toolbar__filter">
                <el-select v-model="selectedTeamId" class="ssh-team-select" placeholder="全部团队" clearable>
                    <el-option v-for="team in visibleTeamList" :key="team.id"
                        :label="`${team.name} 【${teamMatchCounts[team.id] || 0}】`" :value="team.id" />
                </el-select>
            </div>
            <div class="ssh-toolbar__search">
                <el-input v-model="searchKeyword" class="ssh-search" placeholder="搜索服务器或命令" clearable>
                    <template #prefix>
                        <el-icon>
                            <Search />
                        </el-icon>
                    </template>
                </el-input>
            </div>
        </div>
        <!-- 服务器及操作列表：开始 -->
        <div class="ssh-grid no-select">
            <article class="ssh-card" v-for="item in filteredShareList" :key="item">
                <header class="ssh-card__head">
                    <div class="ssh-card__status" :class="item.portOpen ? 'is-online' : 'is-offline'">
                        <el-image class="ssh-card__status-img"
                            :src="require(item.portOpen ? '../../assets/switch/computer_open.svg' : '../../assets/switch/computer_close.svg')"
                            fit="scale-down" />
                    </div>
                    <div class="ssh-card__title">
                        <div class="ssh-card__name" :title="item.name">{{ item.name }}</div>
                        <div class="ssh-card__addr" :title="item.address">{{ item.address }}</div>
                    </div>
                    <span class="ssh-card__count">{{ item.cmdList.length }} 条</span>
                </header>
                <div class="ssh-card__body">
                    <div class="ssh-cmd" v-for="cmd in item.cmdList" :key="cmd" :title="cmdStatusText(cmd)">
                        <span class="ssh-cmd__dot" :class="cmdStatusClass(cmd)"></span>
                        <span class="ssh-cmd__name">{{ cmd.name }}</span>
                        <div class="ssh-cmd__actions" v-if="this.isLogin">
                            <!-- 生产防护锁：长按解锁按钮 -->
                            <el-tooltip v-if="item.prodLock" placement="top"
                                :content="cmd._prodUnlock ? '已解锁，点击右侧按钮执行（5 秒后自动上锁）' : '生产防护锁：长按 1 秒解锁'">
                                <div class="lock-btn"
                                    :class="{ 'is-holding': cmd._holdProgress > 0, 'is-unlocked': cmd._prodUnlock }"
                                    :style="holdRingStyle(cmd)" @mousedown="startHold(item, cmd)"
                                    @mouseup="cancelHold(cmd)" @mouseleave="cancelHold(cmd)"
                                    @touchstart.prevent="startHold(item, cmd)" @touchend="cancelHold(cmd)"
                                    @touchcancel="cancelHold(cmd)" @click="handleLockedClick(item, cmd)">
                                    <el-icon :size="15">
                                        <Unlock v-if="cmd._prodUnlock" />
                                        <Lock v-else />
                                    </el-icon>
                                </div>
                            </el-tooltip>
                            <!-- 执行按钮 -->
                            <el-tooltip placement="top" :content="runTip(item, cmd)">
                                <el-progress :class="['cust-progress', { 'is-disabled': isProdLocked(item, cmd) }]"
                                    type="circle" :percentage="cmd._waitProgress"
                                    :status="cmd._waitProgress == 100 ? 'success' : 'warning'"
                                    :color="cmd._waitProgress == 100 ? 'var(--el-color-success)' : 'var(--el-color-warning)'"
                                    :stroke-width="5" :width="34" @click="handleRunClick(item, cmd, $event)"
                                    :style="{ cursor: isProdLocked(item, cmd) ? 'not-allowed' : (cmd._waitProgress == 100 ? 'pointer' : 'not-allowed') }">
                                    <template #default="{ }">
                                        <span v-if="cmd._waitProgress">
                                            <el-icon v-if="cmd._waitProgress == 100" :size="21"
                                                color="var(--el-color-success)">
                                                <CaretRight />
                                            </el-icon>
                                            <span v-else style="font-size: 12px;">{{ cmd._waitSeconds }}</span>
                                        </span>
                                        <span v-else style="font-size: 12px;">
                                            <el-icon :size="20">
                                                <More />
                                            </el-icon>
                                        </span>
                                    </template>
                                </el-progress>
                            </el-tooltip>
                        </div>
                    </div>
                </div>
                <footer class="ssh-card__foot" v-if="item.description || item.teamList.length > 0">
                    <p class="ssh-card__desc" v-if="item.description">{{ item.description }}</p>
                    <div class="ssh-card__teams" v-if="item.teamList.length > 0">
                        <el-tag class="ssh-card__team" v-for="team in item.teamList" :key="team.id" type="success"
                            size="small">{{ team.name }}</el-tag>
                    </div>
                </footer>
            </article>
        </div>
        <!-- 空状态 -->
        <div class="ssh-empty" v-if="listLoaded && filteredShareList.length === 0">
            <el-icon :size="26">
                <Search />
            </el-icon>
            <p>{{ searchKeyword || selectedTeamId ? '没有匹配的服务器或命令' : '暂无共享的 SSH 命令' }}</p>
        </div>
        <!-- 服务器及操作列表：结束 -->

        <!-- 执行结果抽屉 -->
        <el-drawer v-model="drawerPanel.visible" class="ssh-log-drawer" direction="btt" :show-close="false"
            :before-close="handleClose" size="85%" destroy-on-close="true">
            <template #header="{ close, titleId, titleClass }">
                <div :id="titleId" :class="[titleClass, 'ssh-log-head']">
                    <span class="ssh-log-head__machine">{{ currentRunning.machine.name }}</span>
                    <span class="ssh-log-head__addr">{{ currentRunning.machine.address }}</span>
                    <span class="ssh-log-head__cmd">{{ currentRunning.cmd.name }}</span>
                </div>
                <el-button class="ssh-log-close" type="danger" plain @click="close">
                    <el-icon class="el-icon--left">
                        <CircleCloseFilled />
                    </el-icon>
                    关闭
                </el-button>
            </template>
            <div id="outputContainer" class="ssh-log-body">
                <div class="ssh-log-cmd">
                    <span class="ssh-log-cmd__label">执行指令</span>
                    <code class="ssh-log-cmd__text">{{ currentRunning.cmd.contentTa }}</code>
                </div>
                <div class="ssh-log-output">
                    <v-md-editor v-model="currentRunning.output" mode="preview" ref="editor"
                        @copy-code-success="handleCopyCodeSuccess" />
                </div>
                <div class="ssh-log-done" v-if="this.currentRunning.runFinish">执行指令完成！</div>
            </div>
        </el-drawer>
    </div>
</template>

<script>
import { ElMessageBox, ElMessage } from 'element-plus';
import { Search, Share, Guide, Operation } from '@element-plus/icons';
import request from '../../utils/request.js';
import { config } from '@/utils/config';
import { getToken } from '@/utils/auth';
import _ from 'lodash';
import * as moment from 'moment';
export default {
    data() {
        return {
            user: {},
            isLogin: false,
            searchKeyword: '',
            selectedTeamId: '',
            teamList: [],
            teamStorageKey: 'hidoc_ssh_selected_team_id',
            shareList: [],
            listLoaded: false,
            drawerPanel: {
                visible: false
            },
            currentRunning: {
                machine: {},
                cmd: {},
                log: {},
                output: '',
                prepareOutput: '',
                refreshTimer: '',
                refreshCount: 0,
                runFinish: false,
                serialNumber: 1
            },
            progressRefresh: {
                debug: false,
                refreshTimer: null,
                refreshCount: 0,
            }
        };
    },
    components: { Search },
    watch: {
        selectedTeamId(val) {
            // 记住上次选中的团队，刷新后仍保持（清空时也同步记录）
            localStorage.setItem(this.teamStorageKey, val || '');
        }
    },
    computed: {
        keywordFilteredShareList() {
            const list = this.shareList;
            if (!this.searchKeyword) {
                return list;
            }
            const keywords = this.searchKeyword.trim().split(/\s+/).filter(k => k.length > 0);
            if (keywords.length === 0) {
                return list;
            }
            return list.filter(item => {
                return keywords.every(keyword => {
                    const lowerKeyword = keyword.toLowerCase();
                    const matchServerName = item.name.toLowerCase().includes(lowerKeyword);
                    const matchCmdName = item.cmdList.some(cmd => cmd.name.toLowerCase().includes(lowerKeyword));
                    return matchServerName || matchCmdName;
                });
            });
        },
        filteredShareList() {
            if (!this.selectedTeamId) {
                return this.keywordFilteredShareList;
            }
            return this.keywordFilteredShareList.filter(item =>
                (item.teamList || []).some(team => team.id === this.selectedTeamId)
            );
        },
        teamMatchCounts() {
            // 各团队在当前搜索条件下匹配的服务器数量
            const counts = {};
            this.teamList.forEach(team => {
                counts[team.id] = 0;
            });
            this.keywordFilteredShareList.forEach(item => {
                (item.teamList || []).forEach(team => {
                    if (counts[team.id] !== undefined) {
                        counts[team.id]++;
                    }
                });
            });
            return counts;
        },
        visibleTeamList() {
            // 不展示匹配数量为 0 的团队；当前已选中的团队保留，避免下拉框回显成 id
            return this.teamList.filter(team =>
                team.id === this.selectedTeamId || (this.teamMatchCounts[team.id] || 0) > 0
            );
        }
    },
    created() {
        console.log('created 钩子函数被调用');
    },
    mounted() {
        console.log('mounted 钩子函数被调用');
        document.title = 'Hidoc-SSH';

        this.user = this.$store.state.user;
        if (this.user.token == undefined || this.user.token == '') {
            this.isLogin = false;
            this.user.roles = [];
        } else {
            this.isLogin = true;
        }
        if (this.user.token) {
            this.getCurrentUserInfo();
            this.getTeamFilterOptions();
        }
        this.getShareList();

        const urlParams = new URLSearchParams(window.location.search);
        const keyParam = urlParams.get('key');
        if (keyParam) {
            this.searchKeyword = keyParam;
        }
    },
    unmounted() {
        console.log('unmounted 钩子函数被调用');
        clearInterval(this.progressRefresh.refreshTimer);
        clearInterval(this.currentRunning.refreshTimer);
        console.log('定时器已清除');
    },
    methods: {
        progressRefreshFunction(shareCmdList) {
            this.progressRefresh.refreshCount++;
            if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，第 ${this.progressRefresh.refreshCount} 次`);

            let nextDo = false;
            if (shareCmdList && shareCmdList.length > 0) {
                let now = moment();
                if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，共计 ${shareCmdList.length} 个服务器开放指令，当前时间为：${now.format('YYYY-MM-DD HH:mm:ss')}`);
                for (let i = 0; i < shareCmdList.length; i++) {
                    for (let j = 0; j < shareCmdList[i].cmdList.length; j++) {
                        let pgs = 100; // 进度
                        let sec = 0; // 等待秒数
                        let shareItem = shareCmdList[i];
                        let cmdItem = shareCmdList[i].cmdList[j];
                        if (cmdItem.interval && cmdItem.interval > 1 && cmdItem.executeTime) {
                            let executeTime = moment(cmdItem.executeTime);
                            let nextExecuteTime = moment(cmdItem.executeTime).add(cmdItem.interval, 'seconds');
                            sec = nextExecuteTime.diff(now, 'seconds');
                            pgs = Math.round((1 - (sec / cmdItem.interval)) * 100);

                            if (this.progressRefresh.debug) console.log(`刷新命令执行限制时间，第 ${i + 1} 个服务器，第 ${j + 1} 个指令，执行时间：${executeTime.format('YYYY-MM-DD HH:mm:ss')}，执行间隔：${cmdItem.interval}， 下次执行时间：${nextExecuteTime.format('YYYY-MM-DD HH:mm:ss')}，应该等待：${sec}秒`);
                            if (sec > 0) {
                                nextDo = true;
                                console.log(`${this.progressRefresh.refreshTimer}，服务器：${shareItem.name}，指令：${cmdItem.name}，【冷却进度：${pgs}%】`);
                            }
                        }
                        cmdItem._waitProgress = pgs > 100 ? 100 : pgs;
                        if (sec > 0) cmdItem._waitSeconds = sec;
                    }
                }
            }
            if (nextDo == false) {
                clearInterval(this.progressRefresh.refreshTimer);
                console.log('所有的进度都已经就绪，定时器已清除');
            }
        },
        getCurrentUserInfo() {
            request({
                url: '/user/currentUserInfo',
                method: 'post'
            }).then(res => {
                if (res.code == 0) {
                    this.user = res.meta.user;
                }
            });
        },
        getTeamFilterOptions() {
            const token = this.$store.state.user.token;
            Promise.all([
                request({ url: '/team/getMyJoinTeams', method: 'post', data: { token } }),
                request({ url: '/team/getOwnerTeams', method: 'post', data: { token } })
            ]).then(results => {
                const teamMap = {};
                results.forEach(res => {
                    if (res.code == 0 && res.data) {
                        res.data.forEach(team => {
                            teamMap[team.id] = { id: team.id, name: team.name };
                        });
                    }
                });
                this.teamList = Object.values(teamMap).sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
                // 恢复上次选中的团队（若该团队已不在列表中则忽略）
                const cachedTeamId = localStorage.getItem(this.teamStorageKey);
                if (cachedTeamId && this.teamList.some(team => team.id === cachedTeamId)) {
                    this.selectedTeamId = cachedTeamId;
                }
            });
        },
        getShareList() {
            request({
                url: '/openapi/serverMan/shareList',
                method: 'post',
                data: {
                    token: this.$store.state.user.token
                }
            }).then(res => {
                clearInterval(this.progressRefresh.refreshTimer);
                if (res.code == 0) {
                    let shareList = res.data || [];
                    // 初始化生产防护锁开关状态：默认关闭
                    shareList.forEach(machine => {
                        (machine.cmdList || []).forEach(cmd => {
                            if (cmd._prodUnlock === undefined) cmd._prodUnlock = false;
                        });
                    });
                    this.progressRefreshFunction(shareList);
                    this.shareList = shareList;
                    this.progressRefresh.refreshTimer = setInterval(() => this.progressRefreshFunction(this.shareList), 1000);
                }
                this.listLoaded = true;
            });
        },
        cmdStatusClass(cmd) {
            // 命令端口状态：绿=已开放，红=未开放，灰=未配置端口
            if (!cmd.port) return 'is-none';
            return cmd.portOpen ? 'is-online' : 'is-offline';
        },
        cmdStatusText(cmd) {
            if (!cmd.port) return '未配置端口';
            return cmd.portOpen ? '端口已开放' : '端口未开放';
        },
        runTip(machine, cmd) {
            // 执行按钮悬浮提示：说明当前可执行 / 需等待 / 需先解锁
            if (this.isProdLocked(machine, cmd)) return '生产防护锁已启用，请长按左侧锁钮解锁';
            if (cmd._waitProgress == 100) return '点击执行';
            return `冷却中，${cmd._waitSeconds || 0} 秒后可执行`;
        },
        isProdLocked(machine, cmd) {
            // 生产防护锁：服务器开启后，需长按锁钮解锁才允许执行
            return !!(machine.prodLock && !cmd._prodUnlock);
        },
        holdRingStyle(cmd) {
            // 解锁后：绿色计时环，5 秒内逐渐收掉（露出的红色底环即"已回锁"）
            if (cmd._prodUnlock) {
                const left = cmd._relockProgress === undefined ? 100 : cmd._relockProgress;
                const unlockDeg = left * 3.6;
                return { background: `conic-gradient(#67c23a ${unlockDeg}deg, #f56c6c ${unlockDeg}deg)` };
            }
            // 长按进度：绿色进度环压在红色底环上逐渐填充
            const progress = cmd._holdProgress || 0;
            if (progress <= 0) return {};
            const deg = progress * 3.6;
            return { background: `conic-gradient(#67c23a ${deg}deg, #f56c6c ${deg}deg)` };
        },
        startHold(machine, cmd) {
            if (!machine.prodLock || cmd._prodUnlock) return;
            clearInterval(cmd._holdTimer);
            cmd._holdProgress = 0;
            const holdMs = 1000;
            const startTime = Date.now();
            cmd._holdTimer = setInterval(() => {
                const progress = Math.min(100, Math.round(((Date.now() - startTime) / holdMs) * 100));
                cmd._holdProgress = progress;
                if (progress >= 100) {
                    clearInterval(cmd._holdTimer);
                    cmd._holdTimer = null;
                    cmd._holdProgress = 0;
                    cmd._prodUnlock = true;
                    // 解锁后 5 秒内未执行，自动重新上锁（锁钮上有倒计时环）
                    this.startRelockCountdown(cmd);
                }
            }, 20);
        },
        startRelockCountdown(cmd) {
            // 解锁后的自动上锁倒计时：_relockProgress 由 100 递减到 0
            clearInterval(cmd._relockTickTimer);
            const relockMs = 5000;
            const startTime = Date.now();
            cmd._relockProgress = 100;
            cmd._relockTickTimer = setInterval(() => {
                const left = Math.max(0, 1 - (Date.now() - startTime) / relockMs);
                cmd._relockProgress = Math.round(left * 100);
                if (left <= 0) {
                    this.cancelRelockCountdown(cmd);
                }
            }, 50);
        },
        cancelRelockCountdown(cmd) {
            // 结束倒计时并立即上锁
            clearInterval(cmd._relockTickTimer);
            cmd._relockTickTimer = null;
            cmd._relockProgress = 0;
            cmd._prodUnlock = false;
        },
        cancelHold(cmd) {
            clearInterval(cmd._holdTimer);
            cmd._holdTimer = null;
            cmd._holdProgress = 0;
        },
        handleLockedClick(machine, cmd) {
            // 未解锁时点击锁钮：给一次提示
            if (this.isProdLocked(machine, cmd)) {
                ElMessage({ message: '生产防护锁已启用，请长按锁钮 1 秒解锁', type: 'warning', duration: 3 * 1000 });
            }
        },
        handleRunClick(machine, cmd, event) {
            // 未解锁时点击执行按钮：给一次提示
            if (this.isProdLocked(machine, cmd)) {
                ElMessage({ message: '生产防护锁已启用，请长按锁钮 1 秒解锁', type: 'warning', duration: 3 * 1000 });
                return;
            }
            // 执行后立即上锁
            if (machine.prodLock) {
                this.cancelRelockCountdown(cmd);
            }
            this.runCmd(machine, cmd, event);
        },
        runCmd(machine, cmd, event) {
            ElMessage({ message: `触发执行指令`, type: 'success', duration: 3 * 1000 });
            // ElMessage({ message: `正在执行：${machine.name} ${cmd.name} 指令`, type: 'success', duration: 3 * 1000 });

            console.log('runCmd: ' + cmd.id);
            if (event.shiftKey) {
                console.log(`You clicked with the Shift key pressed`);
            } else {
                console.log(`You clicked without the Shift key pressed`);
            }
            // 重置当前正在运行的命令
            this.currentRunning.serialNumber = 1;
            this.currentRunning.prepareOutput = '';

            return request({
                url: '/serverManCmd/run',
                method: 'post',
                data: { token: this.$store.state.user.token, id: cmd.id }
            }).then(res => {
                if (res.code == 0) {
                    console.log(res);
                    this.currentRunning.log = res.meta.log;
                    if (!event.shiftKey) {
                        this.openDrawerPanel(machine, cmd);
                    }
                    ElMessage({
                        message: res.msg,
                        type: 'success',
                        duration: 3 * 1000
                    });
                }
                this.getShareList();
            });
        },
        refreshCmdLogOutput() {
            let that = this;
            this.currentRunning.refreshCount--;
            if (this.currentRunning.log && this.currentRunning.refreshCount > 0) {
                return request({
                    url: '/serverManExeLog/serverManOutput',
                    method: 'post',
                    data: { token: this.$store.state.user.token, dialogId: this.currentRunning.log.id, serialNumber: this.currentRunning.serialNumber }
                }).then(res => {
                    if (res.code == 0 && res.count > 0) {
                        // 循环组装输出的内容
                        for (let i = 0; i < res.data.length; i++) {
                            // 设置序号为最新值，来防止重复获取数据
                            this.currentRunning.serialNumber = res.data[i].serialNumber + 1;
                            this.currentRunning.prepareOutput = this.currentRunning.prepareOutput + res.data[i].output;

                            // 前端判断是否已经执行成功了，有执行成功的标记则不再刷新了
                            if (res.data[i].output.indexOf('##hidoc->serverman.run::end') > 0) {
                                this.currentRunning.runFinish = true;
                                clearInterval(this.currentRunning.refreshTimer);
                            }
                        }
                        this.currentRunning.output = '```bash' + '\r\n' + this.currentRunning.prepareOutput + '\r\n' + '```';

                        // 应用滚动
                        this.$nextTick(() => {
                            let outputElement = document.getElementById('outputContainer').parentElement;
                            outputElement.scrollTop = outputElement.scrollHeight - outputElement.clientHeight;
                        });
                    }
                });
            }
        },
        // refreshCmdLogOutput() {
        //     this.currentRunning.refreshCount--;
        //     if (this.currentRunning.log && this.currentRunning.refreshCount > 0) {
        //         return request({
        //             url: '/serverManExeLog/fileDetail',
        //             method: 'post',
        //             data: { token: this.$store.state.user.token, id: this.currentRunning.log.id }
        //         }).then(res => {
        //             if (res.code == 0) {
        //                 this.currentRunning.output = '```bash' + '\r\n' + res.meta.fileDetail + '\r\n' + '```';

        //                 // 前端判断是否已经执行成功了，有执行成功的标记则不再刷新了
        //                 if (res.meta.fileDetail.indexOf('##hidoc->serverman.run::end') > 0) {
        //                     this.currentRunning.runFinish = true;
        //                     clearInterval(this.currentRunning.refreshTimer);
        //                 }

        //                 // 应用滚动
        //                 this.$nextTick(() => {
        //                     let outputElement = document.getElementById('outputContainer').parentElement;
        //                     outputElement.scrollTop = outputElement.scrollHeight - outputElement.clientHeight;
        //                 });
        //                 // console.log(`scrollHeight: ${outputElement.scrollHeight}, scrollTop: ${outputElement.scrollTop}, clientHeight: ${outputElement.clientHeight}`);
        //                 // 这里根据高度判断来向下滚动不合适，输出的文本会突然变大，把高度撑的很高，就没有办法满足吸附条件了
        //                 // if (outputElement.scrollHeight - outputElement.scrollTop - outputElement.clientHeight < 1000) {
        //                 //     console.log('滚动条已经滚动到底部，将自动向下滚动');
        //                 //     outputElement.scrollTop = outputElement.scrollHeight - outputElement.clientHeight;
        //                 // }
        //             }
        //         });
        //     }
        // },
        openDrawerPanel(machine, cmd) {
            this.currentRunning.machine = machine;
            this.currentRunning.cmd = cmd;
            this.currentRunning.output = '```bash' + '\r\n' + '... ... 正在获取结果信息 ... ...' + '\r\n' + '```';
            this.drawerPanel.visible = true;
            this.currentRunning.refreshCount = 100;
            this.currentRunning.refreshTimer = setInterval(this.refreshCmdLogOutput, 2 * 1000);
        },
        handleClose() {
            ElMessageBox.confirm('确定要关闭抽屉吗？')
                .then(() => {
                    this.drawerPanel.visible = false;
                    clearInterval(this.currentRunning.refreshTimer);

                    // this.currentRunning.machine = {};
                    // this.currentRunning.cmd = {};
                })
                .catch(() => {
                    // catch error
                });
        },
        handleCopyCodeSuccess(code) {
            // console.log(code);
            ElMessage({
                message: '复制成功',
                type: 'success',
                duration: 1 * 1000
            });
        }
    }
};
</script>

<style lang="scss" scoped>
.ssh-page {
    /* 局部覆盖主色，与登录页、首页保持同一套视觉语言 */
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --ssh-text: #1f2d3d;
    --ssh-text-sub: #5b6a80;
    --ssh-text-weak: #8a94a6;
    --ssh-line: #e6eaf0;

    /* 浅色画布由外层的 .app-main-body 提供，这里只负责内容排布 */
    color: var(--ssh-text);
}

/* ---------- 未登录提示 ---------- */
.ssh-notice {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 10px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary-dark-2);
    font-size: 13px;
}

.ssh-notice__icon {
    flex: 0 0 auto;
    font-size: 15px;
}

/* ---------- 工具条：团队筛选 + 搜索 ---------- */
.ssh-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
}

.ssh-toolbar__filter {
    flex: 0 0 auto;
}

.ssh-team-select {
    width: 240px;
}

.ssh-team-select :deep(.el-select__wrapper) {
    border-radius: 10px;
    background-color: #ffffff;
    box-shadow: 0 0 0 1px var(--ssh-line) inset;
    transition: box-shadow 0.2s;
}

.ssh-team-select :deep(.el-select__wrapper:hover) {
    box-shadow: 0 0 0 1px #cfd8e6 inset;
}

.ssh-team-select :deep(.el-select__wrapper.is-focused) {
    box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

.ssh-toolbar__search {
    /* 未登录时工具条内只剩搜索框，margin-left: auto 保证它仍然靠右 */
    flex: 0 1 300px;
    min-width: 200px;
    margin-left: auto;
}

.ssh-search :deep(.el-input__wrapper) {
    border-radius: 10px;
    background-color: #ffffff;
    box-shadow: 0 0 0 1px var(--ssh-line) inset;
    transition: box-shadow 0.2s;
}

.ssh-search :deep(.el-input__wrapper:hover) {
    box-shadow: 0 0 0 1px #cfd8e6 inset;
}

.ssh-search :deep(.el-input__wrapper.is-focus) {
    box-shadow: 0 0 0 1px var(--el-color-primary) inset;
}

/* ---------- 服务器卡片瀑布流 ---------- */
/* 多栏布局：卡片按列自上而下紧密排列，行与行之间不会因为"对齐最高卡片"而留出空白 */
.ssh-grid {
    column-width: 300px;
    column-gap: 16px;
}

.ssh-card {
    /* 多栏布局中列间距用 column-gap，卡片之间的纵向间距只能靠 margin */
    margin: 0 0 16px;
    /* 避免卡片被跨栏截断 */
    break-inside: avoid;
    border: 1px solid var(--ssh-line);
    border-radius: 14px;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
    overflow: hidden;
    transition:
        box-shadow 0.25s,
        transform 0.25s,
        border-color 0.25s;
}

.ssh-card:hover {
    border-color: #d8e1ef;
    box-shadow: 0 14px 30px -16px rgba(28, 47, 84, 0.3);
    transform: translateY(-2px);
}

/* 卡片头：状态图标 + 名称地址 + 命令数 */
.ssh-card__head {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.ssh-card__status {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background-color: #f2f5fa;
    box-shadow: 0 0 0 1px var(--ssh-line) inset;
}

.ssh-card__status.is-online {
    background-color: #f0f9eb;
    box-shadow: 0 0 0 1px #e1f3d8 inset;
}

.ssh-card__status-img {
    width: 28px;
    height: 18px;
}

.ssh-card__title {
    flex: 1 1 auto;
    min-width: 0;
}

.ssh-card__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    color: var(--ssh-text);
}

.ssh-card__addr {
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #aab4c4;
}

.ssh-card__count {
    flex: 0 0 auto;
    padding: 2px 8px;
    border-radius: 999px;
    background-color: var(--el-color-primary-light-9);
    color: var(--el-color-primary);
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
}

/* 卡片体：命令行列表 */
.ssh-card__body {
    padding: 6px;
}

.ssh-cmd {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 7px 8px;
    border-radius: 8px;
    transition: background-color 0.2s;
}

.ssh-cmd:hover {
    background-color: #f6f9ff;
}

/* 状态圆点：绿=端口已开放，红=端口未开放，灰=未配置端口 */
.ssh-cmd__dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #d8dee8;
}

.ssh-cmd__dot.is-online {
    background-color: #67c23a;
    box-shadow: 0 0 0 3px rgba(103, 194, 58, 0.15);
}

.ssh-cmd__dot.is-offline {
    background-color: #f56c6c;
    box-shadow: 0 0 0 3px rgba(245, 108, 108, 0.15);
}

.ssh-cmd__name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: var(--ssh-text-sub);
    transition: color 0.2s;
}

.ssh-cmd:hover .ssh-cmd__name {
    color: var(--ssh-text);
}

/* 生产防护锁：解锁按钮与执行按钮并排（靠右对齐） */
.ssh-cmd__actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
}

/* 卡片脚：说明 + 所属团队 */
.ssh-card__foot {
    padding: 10px 14px 12px;
    border-top: 1px solid #eef1f6;
    background-color: #fbfcfe;
}

.ssh-card__desc {
    margin: 0;
    font-size: 12px;
    line-height: 1.5;
    color: var(--ssh-text-weak);
}

.ssh-card__teams {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}

.ssh-card__team {
    margin: 0;
}

/* ---------- 空状态 ---------- */
.ssh-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 56px 20px;
    border: 1px dashed #dcdfe6;
    border-radius: 12px;
    background-color: #ffffff;
    color: #909399;
    font-size: 13px;
}

.ssh-empty p {
    margin: 0;
}

/* ---------- 生产防护锁：长按解锁按钮（环形填充） ---------- */
.lock-btn {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 8px;
    border-radius: 50%;
    /* 默认（锁定）：红色锁 + 红色底环 */
    background: #f56c6c;
    color: #f56c6c;
    cursor: pointer;
    user-select: none;
    transition:
        color 0.15s,
        background-color 0.15s;
}

/* 白色内芯，形成环状底槽（inset 即环的粗细，环厚 = inset，内孔 = 外径 - 2 × inset） */
.lock-btn::before {
    content: '';
    position: absolute;
    inset: 5px;
    border-radius: 50%;
    background: #ffffff;
}

.lock-btn .el-icon {
    position: relative;
    z-index: 1;
}

/* 长按中：绿色进度环由 holdRingStyle 实时绘制，锁图标保持红色（此时仍未解锁） */

/* 已解锁：绿色开锁图标（环的颜色由 holdRingStyle 按倒计时实时绘制） */
.lock-btn.is-unlocked {
    color: #67c23a;
}

/* 执行按钮 */
.cust-progress {
    flex: 0 0 auto;
}

.cust-progress :deep(.el-progress__text) {
    min-width: 0;
}

/* 未解锁时执行按钮置灰 */
.cust-progress.is-disabled {
    filter: grayscale(1);
    opacity: 0.5;
}

/* 滚动条：限定在页面作用域内，避免影响全站 */
.ssh-page ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #d8d8d8;
}

.ssh-page ::-webkit-scrollbar-track {
    border-radius: 10px;
}

.ssh-page ::-webkit-scrollbar-thumb {
    background-color: #bfc1c4;
}

/* ---------- 移动端适配 ---------- */
@media (max-width: 768px) {
    .ssh-toolbar {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }

    .ssh-toolbar__filter,
    .ssh-toolbar__search {
        flex: 1 1 auto;
        width: 100%;
        min-width: 0;
        margin-left: 0;
    }

    .ssh-team-select {
        width: 100%;
    }

    /* 窄屏下 column-width 自动退化为单栏，只需要收紧卡片间距 */
    .ssh-card {
        margin-bottom: 12px;
    }

    .ssh-card__head {
        padding: 10px 12px;
    }

    .ssh-card__foot {
        padding: 10px 12px;
    }

    /* 移动端不依赖 hover，同时把触摸区域放大一些 */
    .ssh-cmd {
        padding: 9px 8px;
    }
}
</style>

<!-- 抽屉会挂到 body 下，样式需要放在非 scoped 块里，并统一带 .ssh-log-drawer 前缀 -->
<style lang="scss">
.ssh-log-drawer {
    overflow: hidden;
    border-radius: 16px 16px 0 0;
}

.ssh-log-drawer .el-drawer__header {
    gap: 12px;
    margin-bottom: 0;
    padding: 14px 18px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.ssh-log-drawer .el-drawer__title {
    margin: 0;
}

.ssh-log-drawer .el-drawer__body {
    padding: 16px 18px 24px;
}

.ssh-log-drawer .ssh-log-head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.ssh-log-drawer .ssh-log-head__machine {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
}

.ssh-log-drawer .ssh-log-head__addr {
    font-size: 13px;
    color: #8a94a6;
}

.ssh-log-drawer .ssh-log-head__cmd {
    padding: 2px 10px;
    border-radius: 999px;
    background-color: #eaf1fd;
    color: #2f6fed;
    font-size: 12px;
    font-weight: 500;
}

.ssh-log-drawer .ssh-log-close {
    flex: 0 0 auto;
    height: 34px;
    border-radius: 9px;
}

/* 执行指令 */
.ssh-log-drawer .ssh-log-cmd {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    border: 1px solid #eef1f6;
    border-radius: 10px;
    background-color: #f7f9fc;
}

.ssh-log-drawer .ssh-log-cmd__label {
    flex: 0 0 auto;
    font-size: 13px;
    font-weight: 600;
    color: #f56c6c;
}

.ssh-log-drawer .ssh-log-cmd__text {
    flex: 1 1 auto;
    min-width: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 13px;
    line-height: 1.5;
    color: #1f2d3d;
    white-space: pre-wrap;
    word-break: break-all;
}

/* 执行结果：横向内边距清零，与上方指令块左对齐 */
.ssh-log-drawer .ssh-log-output {
    margin-top: 12px;
}

.ssh-log-drawer .ssh-log-output .vuepress-markdown-body {
    overflow: hidden;
    padding-left: 0;
    padding-right: 0;
}

.ssh-log-drawer .ssh-log-done {
    margin-top: 20px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #67c23a;
}
</style>
